import {
  Drawer as ChakraDrawer,
  DrawerBody,
  DrawerOverlay,
  DrawerContent,
  DrawerHeader,
  Text,
  Skeleton,
  IconButton,
  HStack,
} from '@chakra-ui/react';
import React from 'react';

import { finleyAssertUnreachable } from '@finley-technologies/common';

import { PendingFundingRequests } from './PendingFundingRequests';
import { Icon } from '~/packages/common-frontend/components/icons';
import { Typography } from '~/packages/common-frontend/components/Typography';
import { useFundingRequestTemplateContextV1 } from '~/packages/funding-requests/hooks/useFundingRequestTemplateContextV1';
import { FundingRequestDetailBorrowingBaseSummary } from '~/packages/funding-requests/ui/FundingRequestDetailBorrowingBaseSummary';

export type DrawerType =
  | 'borrowingBase'
  | 'pendingDrawRequests'
  | 'pendingRecycleRequests';

export const DrawerContents = ({
  type,
}: {
  type: DrawerType | null;
}): React.ReactElement | null => {
  const { template, creditFacility } = useFundingRequestTemplateContextV1();

  switch (type) {
    case 'borrowingBase':
      return template && creditFacility ? (
        <FundingRequestDetailBorrowingBaseSummary
          hasTitle={false}
          borrowingBaseReportId={template.borrowingBaseReport.id}
          creditFacilityId={creditFacility.id}
        />
      ) : (
        <Skeleton sx={{ h: '100%', w: '100%' }} />
      );
    case 'pendingDrawRequests':
      return <PendingFundingRequests type="drawRequest" />;
    case 'pendingRecycleRequests':
      return <PendingFundingRequests type="collectionsRecycle" />;
    case null:
      return null;
    default:
      return finleyAssertUnreachable(type);
  }
};

const DrawerTitle = ({ type }: { type: DrawerType | null }) => {
  switch (type) {
    case 'borrowingBase':
      return <Text sx={{ fontSize: '1.5em' }}>Borrowing Base</Text>;
    case 'pendingDrawRequests':
      return <Text sx={{ fontSize: '1.5em' }}>Pending Draw Requests</Text>;
    case 'pendingRecycleRequests':
      return <Text sx={{ fontSize: '1.5em' }}>Pending Recycle Requests</Text>;
    case null:
    default:
      return null;
  }
};

type DrawerProps = {
  drawerType: DrawerType | null;
  onClose: () => void;
  isOpen: boolean;
  finalFocusRef: React.RefObject<HTMLButtonElement> | undefined;
  subTitle?: string;
};

export const Drawer = ({
  drawerType,
  onClose,
  isOpen,
  finalFocusRef,
  subTitle,
}: DrawerProps) => {
  return (
    <ChakraDrawer
      isOpen={isOpen}
      placement="right"
      onClose={onClose}
      finalFocusRef={finalFocusRef}
      size="xl"
    >
      <DrawerOverlay />
      <DrawerContent sx={{ p: '2em', overflowY: 'scroll' }}>
        <DrawerHeader
          as={HStack}
          sx={{ justifyContent: 'space-between', w: '100%' }}
        >
          <DrawerTitle type={drawerType} />
          <IconButton
            aria-label="close borrowing base drawer"
            icon={<Icon icon="x" />}
            onClick={onClose}
            variant="ghost"
            colorScheme="v2-aurora"
          />
        </DrawerHeader>
        {subTitle && (
          <Typography
            variant="base-muted"
            colorScheme="gray"
            TextProps={{ padding: '16px 24px 24px 24px' }}
          >
            {subTitle}
          </Typography>
        )}

        <DrawerBody>
          <DrawerContents type={drawerType} />
        </DrawerBody>
      </DrawerContent>
    </ChakraDrawer>
  );
};
